import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { usePersistedAuthStore } from '../store/usePersistedAuthStore';
import { useUserStore } from '../store/useUserStore';
import { useTenantStore } from '../store/useTenantStore';
import { apiClient } from '../lib/api/client';
import { ApiResponse, LoginResponse, SessionInfo } from '../lib/api/types';
import { useSanitizedInput } from '../hooks/useSanitizedInput';
import { FormDataSanitizer } from '../lib/security/sanitizer';
import { 
  User, 
  Lock, 
  QrCode, 
  Tablet, 
  LogIn, 
  ArrowLeft,
  AlertCircle,
  Monitor,
  Smartphone,
  Hash
} from 'lucide-react';
import '../styles/animations.css';

interface LoginFormProps {
  onSwitchToWhiteboard: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToWhiteboard }) => {
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const { setRefreshToken, setUserId, setShouldChangePassword } = usePersistedAuthStore();
  const setUserInfo = useUserStore((state) => state.setUserInfo);
  
  // Use sanitized inputs
  const usernameInput = useSanitizedInput('', {
    type: 'username',
    required: true,
    debounceMs: 300
  });
  
  const passwordInput = useSanitizedInput('', {
    required: true
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate inputs
    if (!usernameInput.isValid || !passwordInput.isValid) {
      setError('Lütfen tüm alanları doğru şekilde doldurun');
      return;
    }
    
    // Sanitize form data
    const sanitizedData = FormDataSanitizer.sanitizeLoginForm({
      username: usernameInput.sanitizedValue,
      password: passwordInput.value, // Don't sanitize password
      rememberMe: true
    });
    
    if (!sanitizedData || !sanitizedData.username || !sanitizedData.password) {
      setError('Geçersiz giriş bilgileri');
      return;
    }
    
    setLoading(true);

    try {
      // Login with sanitized data
      const loginResponse = await apiClient.login(
        sanitizedData.username,
        sanitizedData.password
      );
      const loginData = loginResponse.data as ApiResponse<LoginResponse>;

      if (!loginData.success || !loginData.result) {
        throw new Error(loginData.error?.message || 'Giriş başarısız');
      }

      const { accessToken, expireInSeconds, refreshToken, userId, shouldChangePasswordOnNextLogin } = loginData.result;

      // Set auth data
      setAccessToken(accessToken, expireInSeconds);
      setRefreshToken(refreshToken);
      setUserId(userId);
      setShouldChangePassword(shouldChangePasswordOnNextLogin);

      // Get user info
      const userInfoResponse = await apiClient.getCurrentLoginInfo();
      const userInfoData = userInfoResponse.data as ApiResponse<SessionInfo>;

      if (userInfoData.success && userInfoData.result?.user) {
        setUserInfo({
          id: userInfoData.result.user.id,
          userName: userInfoData.result.user.userName,
          name: userInfoData.result.user.name,
          surname: userInfoData.result.user.surname,
          emailAddress: userInfoData.result.user.emailAddress,
          isActive: userInfoData.result.user.isActive,
          fullName: `${userInfoData.result.user.name} ${userInfoData.result.user.surname}`.trim(),
          lastLoginTime: userInfoData.result.user.lastLoginTime ? new Date(userInfoData.result.user.lastLoginTime) : null,
          creationTime: new Date(userInfoData.result.user.creationTime),
          roleNames: userInfoData.result.user.roleNames || [],
        });
      }

      // Navigate to library or password reset
      if (shouldChangePasswordOnNextLogin) {
        navigate('/reset-password');
      } else {
        navigate('/library');
      }
    } catch (err: any) {
      setError(err.message || 'Giriş yapılırken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div className="space-y-5">
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
            Kullanıcı Adı veya E-posta
          </label>
          <div className="relative">
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              required
              value={usernameInput.value}
              onChange={(e) => usernameInput.handleChange(e.target.value)}
              onBlur={usernameInput.handleBlur}
              className={`
                block w-full px-4 py-3 pl-11 
                bg-gray-50/50 backdrop-blur-sm
                border rounded-2xl
                text-gray-900 placeholder-gray-400
                transition-all duration-200
                ${usernameInput.showError
                  ? 'border-red-400 ring-4 ring-red-400/20'
                  : 'border-gray-200 hover:border-gray-300 focus:border-purple-400 focus:ring-4 focus:ring-purple-400/20'
                }
                focus:outline-none
              `}
              placeholder="kullanici@ornek.com"
            />
            <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          </div>
          {usernameInput.showError && (
            <p className="mt-2 text-sm text-red-600">{usernameInput.error}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Şifre
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={passwordInput.value}
              onChange={(e) => passwordInput.handleChange(e.target.value)}
              onBlur={passwordInput.handleBlur}
              className={`
                block w-full px-4 py-3 pl-11 
                bg-gray-50/50 backdrop-blur-sm
                border rounded-2xl
                text-gray-900 placeholder-gray-400
                transition-all duration-200
                ${passwordInput.showError
                  ? 'border-red-400 ring-4 ring-red-400/20'
                  : 'border-gray-200 hover:border-gray-300 focus:border-purple-400 focus:ring-4 focus:ring-purple-400/20'
                }
                focus:outline-none
              `}
              placeholder="••••••••"
            />
            <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          </div>
          {passwordInput.showError && (
            <p className="mt-2 text-sm text-red-600">{passwordInput.error}</p>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className={`
          relative w-full
          px-6 py-3
          bg-gradient-to-r from-purple-600 to-blue-600
          text-white font-medium
          rounded-2xl
          transition-all duration-200
          hover:shadow-lg hover:shadow-purple-500/25
          active:scale-[0.98]
          disabled:opacity-50 disabled:cursor-not-allowed
          disabled:hover:shadow-none
          group
        `}
      >
        <span className="flex items-center justify-center gap-2">
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Giriş yapılıyor...</span>
            </>
          ) : (
            <>
              <LogIn className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span>Giriş Yap</span>
            </>
          )}
        </span>
      </button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white/80 text-gray-500">veya</span>
        </div>
      </div>

      <button
        type="button"
        onClick={onSwitchToWhiteboard}
        className={`
          w-full
          px-6 py-3
          bg-gray-50 hover:bg-gray-100
          border border-gray-200
          text-gray-700 font-medium
          rounded-2xl
          transition-all duration-200
          hover:shadow-md
          active:scale-[0.98]
          group
        `}
      >
        <span className="flex items-center justify-center gap-2">
          <QrCode className="w-5 h-5 text-purple-600 group-hover:scale-110 transition-transform" />
          <span>Tahta için QR/Kod ile Giriş</span>
        </span>
      </button>
    </form>
  );
};

interface WhiteboardLoginFormProps {
  onSwitchToNormal: () => void;
}

const WhiteboardLoginForm: React.FC<WhiteboardLoginFormProps> = ({ onSwitchToNormal }) => {
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const { setRefreshToken, setUserId } = usePersistedAuthStore();
  const setUserInfo = useUserStore((state) => state.setUserInfo);
  
  const [code, setCode] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const createSession = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.createWhiteboardSession();
      const data = response.data as ApiResponse<any>;

      if (data.success && data.result) {
        setCode(data.result.code);
        // Generate QR code URL
        const qrData = `${data.result.code}`;
        setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}`);

        // Start checking session status
        startSessionCheck(data.result.code,  data.result.sessionId);
      } else {
        setError('Oturum oluşturulamadı');
      }
    } catch (err) {
      setError('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const startSessionCheck = (code: string, sessionId: string = '') => {
    setChecking(true);
    
    const checkInterval = setInterval(async () => {
      try {
        const response = await apiClient.checkWhiteboardSession(code,sessionId);
        const data = response.data as ApiResponse<any>;

        if (data.success && data.result) {
          if (data.result.isAuthorized === true) {
            clearInterval(checkInterval);
            await getWhiteboardToken(code, sessionId);
          } else if (data.result.isExpired === true) {
            clearInterval(checkInterval);
            setError('Oturum süresi doldu. Lütfen tekrar deneyin.');
            setChecking(false);
          }
        }
      } catch (err) {
      }
    }, 2000);

    // Clear interval after 5 minutes
    setTimeout(() => {
      clearInterval(checkInterval);
      setChecking(false);
      setError('Oturum süresi doldu. Lütfen tekrar deneyin.');
    }, 5 * 60 * 1000);
  };

  const getWhiteboardToken = async (code: string, sessionId: string = '') => {
    try {
      const response = await apiClient.getWhiteboardToken(code,sessionId);
      const data = response.data as ApiResponse<LoginResponse>;

      if (data.success && data.result) {
        const { accessToken, expireInSeconds, refreshToken, userId } = data.result;

        // Set auth data
        setAccessToken(accessToken, expireInSeconds);
        setRefreshToken(refreshToken);
        setUserId(userId);

        // Get user info
        const userInfoResponse = await apiClient.getCurrentLoginInfo();
        const userInfoData = userInfoResponse.data as ApiResponse<SessionInfo>;

        if (userInfoData.success && userInfoData.result?.user) {
          setUserInfo({
            id: userInfoData.result.user.id,
            userName: userInfoData.result.user.userName,
            name: userInfoData.result.user.name,
            surname: userInfoData.result.user.surname,
            emailAddress: userInfoData.result.user.emailAddress,
            isActive: userInfoData.result.user.isActive,
            fullName: `${userInfoData.result.user.name} ${userInfoData.result.user.surname}`.trim(),
            lastLoginTime: userInfoData.result.user.lastLoginTime ? new Date(userInfoData.result.user.lastLoginTime) : null,
            creationTime: new Date(userInfoData.result.user.creationTime),
            roleNames: userInfoData.result.user.roleNames || [],
          });
        }

        navigate('/library');
      }
    } catch (err) {
      setError('Giriş yapılamadı');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-6">
      {!code ? (
        <>
          <div className="text-center space-y-4">
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Monitor className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Tahta Girişi</h3>
              <p className="text-gray-600 text-sm">
                Akıllı tahta veya büyük ekranlar için hızlı giriş
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <QrCode className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-xs text-gray-700 font-medium">QR Kod</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Hash className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-xs text-gray-700 font-medium">6 Haneli Kod</p>
              </div>
            </div>
          </div>

          <button
            onClick={createSession}
            disabled={loading}
            className={`
              relative w-full
              px-6 py-3
              bg-gradient-to-r from-purple-600 to-blue-600
              text-white font-medium
              rounded-2xl
              transition-all duration-200
              hover:shadow-lg hover:shadow-purple-500/25
              active:scale-[0.98]
              disabled:opacity-50 disabled:cursor-not-allowed
              disabled:hover:shadow-none
              group
            `}
          >
            <span className="flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Oluşturuluyor...</span>
                </>
              ) : (
                <>
                  <QrCode className="w-5 h-5" />
                  <span>QR Kod Oluştur</span>
                </>
              )}
            </span>
          </button>
        </>
      ) : (
        <div className="text-center space-y-6">
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
            {qrCodeUrl && (
              <div className="mb-6">
                <div className="bg-white p-4 rounded-xl inline-block shadow-lg">
                  <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">veya bu kodu girin:</p>
              <div className="text-5xl font-bold text-gray-900 tracking-[0.3em] bg-gray-100 rounded-2xl py-4 px-6 inline-block">
                {code}
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-purple-600">
              <Smartphone className="w-5 h-5" />
              <p className="text-sm font-medium">
                Mobil uygulamadan {checking ? 'onay bekleniyor...' : 'bu kodu girin'}
              </p>
            </div>

            {checking && (
              <div className="mt-4">
                <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto"></div>
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-2xl bg-red-50 border border-red-200 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={onSwitchToNormal}
        className="w-full flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Normal giriş yap</span>
      </button>
    </div>
  );
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [isWhiteboardLogin, setIsWhiteboardLogin] = useState(false);
  const tenantStore = useTenantStore();
  
  const handleChangeTenant = () => {
    tenantStore.clearTenant();
    navigate('/org-code');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
        
        {/* Animated gradient mesh */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob" />
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000" />
          <div className="absolute bottom-0 right-20 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-4000" />
        </div>
        
        {/* Subtle floating particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-gray-400 rounded-full opacity-20"
              style={{
                left: `${20 + i * 15}%`,
                top: `${10 + i * 10}%`,
                animation: `float ${15 + i * 2}s ease-in-out infinite`,
                animationDelay: `${i * 2}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo and Title */}
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-blue-500 rounded-3xl blur-2xl opacity-20" />
                <img 
                  src="/tahta-icon-minimal.svg" 
                  alt="Tahta" 
                  className="relative w-24 h-24 filter drop-shadow-lg"
                />
              </div>
            </div>
            <h2 className="text-3xl font-semibold text-gray-900 tracking-tight">
              Giriş Yap
            </h2>
            {tenantStore.formattedCode && (
              <p className="mt-2 text-sm text-gray-600">
                {tenantStore.formattedCode.toUpperCase()} kurumuna giriş yapıyorsunuz
              </p>
            )}
          </div>

          {/* Main Card with Glass Effect */}
          <div className="mt-8 relative">
            {/* Card shadow and blur effect */}
            <div className="absolute inset-0 bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl" />
            
            {/* Card content */}
            <div className="relative bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-gray-200/50 p-8">
              {isWhiteboardLogin ? (
                <WhiteboardLoginForm onSwitchToNormal={() => setIsWhiteboardLogin(false)} />
              ) : (
                <LoginForm onSwitchToWhiteboard={() => setIsWhiteboardLogin(true)} />
              )}
            </div>
          </div>
          
          {/* Change Organization Link */}
          <div className="mt-6 text-center">
            <button
              onClick={handleChangeTenant}
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Kurum değiştir
            </button>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center space-y-4">
            <div className="flex items-center justify-center gap-6 text-sm">
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-900 transition-colors"
              >
                Gizlilik
              </a>
              <span className="text-gray-300">•</span>
              <a
                href="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-900 transition-colors"
              >
                Koşullar
              </a>
            </div>
            <p className="text-xs text-gray-400">
              © 2025 Edesis Technology. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export { LoginPage };